// ────────────────────────────────────────────────────────────────────
// What the two import modals share: the rejects table, the rejects.csv the
// operator fixes their spreadsheet from, and the 409-conflict unwrapping.
// ────────────────────────────────────────────────────────────────────

import type { ImportReject, ImportDbConflict } from "@/lib/types";

/** Client-side rejects.csv — the fix loop is "correct the Excel and re-upload",
 *  so the operator gets every refused row with its source row number. The BOM
 *  makes Excel decode Thai correctly (same rule as the export bundle's utf-8-sig).
 *  Value columns derive from the first reject's values keys (parser-specific:
 *  thickness = asset_tag/cml_no/thickness/measured_at · register = plant/area/
 *  asset_tag/asset_class/first_service_date — every reject carries all keys). */
export function downloadRejectsCsv(orig: string, rejects: ImportReject[]): void {
  const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
  const keys = Object.keys(rejects[0]?.values ?? {});
  const lines = [["row", "reason", ...keys].join(","),
    ...rejects.map((x) => [x.row, x.reason, ...keys.map((k) => x.values[k])]
      .map((v) => esc(String(v ?? ""))).join(","))];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = orig.replace(/\.[^.]+$/, "") + "-rejects.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}
/** Fully blank xlsx rows land as no_key rejects. Blankness is judged on the
 *  displayed columns: in the thickness flow the intake preview injects the
 *  filed asset's tag into every row, so asset_tag is non-empty even on a blank
 *  row — that's why it is NOT one of the thickness columns below. */
export function isBlankReject(x: ImportReject, keys: readonly string[]): boolean {
  return x.reason === "no_key" && keys.every((k) => !x.values[k]);
}
/** Table columns per import flow — keys into ImportReject.values. */
export const THICKNESS_REJECT_COLS: readonly { key: string; label: string }[] = [
  { key: "cml_no", label: "CML" }, { key: "thickness", label: "Thk" }, { key: "measured_at", label: "Date" }];
export const REGISTER_REJECT_COLS: readonly { key: string; label: string }[] = [
  { key: "plant", label: "Plant" }, { key: "area", label: "Area" }, { key: "asset_tag", label: "Tag" },
  { key: "asset_class", label: "Class" }, { key: "first_service_date", label: "In-service" }];
/** Rejected-rows detail: table of the first 8 real rejects; fully blank rows
 *  collapse into one summary line (they still ride the CSV for completeness).
 *  Column-agnostic: pass `columns` for non-thickness flows (register import). */
export function RejectsBlock({ rejects, filename, columns = THICKNESS_REJECT_COLS }: {
  rejects: ImportReject[]; filename: string; columns?: readonly { key: string; label: string }[];
}): JSX.Element {
  const keys = columns.map((c) => c.key);
  const real = rejects.filter((x) => !isBlankReject(x, keys));
  const blanks = rejects.length - real.length;
  return (
    <div className="mb-2 px-3 py-2" style={{ background: "rgba(var(--accent-rgb) / .05)", border: "1px solid rgba(var(--accent-rgb) / .4)" }}>
      <div className="flex items-center justify-between">
        <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--warn)", fontWeight: 700 }}>
          {rejects.length} rows rejected
        </span>
        <button onClick={() => downloadRejectsCsv(filename, rejects)}
                className="font-mono uppercase px-2 py-0.5 transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)]"
                style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--ink-2)", border: "1px solid var(--hairline)" }}>
          Download rejects.csv
        </button>
      </div>
      {real.length > 0 && (
        <table className="w-full font-mono mt-1" style={{ fontSize: 10 }}>
          <thead><tr style={{ color: "var(--ink-3)" }}><th align="left">Row</th><th align="left">Reason</th>
            {columns.map((c) => <th key={c.key} align="left">{c.label}</th>)}</tr></thead>
          <tbody>
            {real.slice(0, 8).map((x) => (
              <tr key={x.row}><td>{x.row}</td><td>{x.reason}</td>
                {columns.map((c) => <td key={c.key}>{x.values[c.key]}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )}
      {real.length > 8 && (
        <div className="font-mono" style={{ fontSize: 9, color: "var(--ink-3)" }}>
          +{real.length - 8} more — see the CSV
        </div>
      )}
      {blanks > 0 && (
        <div className="font-mono mt-0.5" style={{ fontSize: 9, color: "var(--ink-3)" }}>
          {blanks} empty rows skipped
        </div>
      )}
    </div>
  );
}
/** The commit 409's dict detail rides axios as error.response.data.detail —
 *  {message, db_conflicts}. Duck-typed so the mutation error needs no cast. */
export function conflictsFrom409(e: unknown): ImportDbConflict[] | null {
  const resp = (e as { response?: { status?: number; data?: { detail?: { db_conflicts?: unknown } } } } | null)?.response;
  const list = resp?.data?.detail?.db_conflicts;
  return resp?.status === 409 && Array.isArray(list) ? (list as ImportDbConflict[]) : null;
}
